// ─────────────────────────────────────────────────────────────────────────────
import cfg from "../config";
const getTokenKey = () => `${cfg.skuPrefix.toLowerCase()}_token`;

// services/api.js  —  ALL API calls to your .NET backend
//
// Real endpoints (from Swagger):
//   POST   /api/Auth/login
//   GET    /api/Categories
//   POST   /api/Categories
//   PUT    /api/Categories/{id}
//   DELETE /api/Categories/{id}
//   GET    /api/Products
//   POST   /api/Products
//   GET    /api/Products/featured
//   GET    /api/Products/{slug}
//   PUT    /api/Products/{id}
//   DELETE /api/Products/{id}
//   POST   /api/Inquiries
//   GET    /api/Inquiries
//   PUT    /api/Inquiries/{id}/status
//
// Setup:
//   Create .env in frontend root:
//     REACT_APP_API_BASE=https://localhost:7001/api
//   Then npm start
// ─────────────────────────────────────────────────────────────────────────────

const BASE = process.env.REACT_APP_API_BASE || "https://localhost:7001/api";

// Returns stored JWT or null
const getToken = () => localStorage.getItem(getTokenKey());

// ── Core fetch wrapper ───────────────────────────────────────────────────────
async function api(path, options = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    let errMsg = `Request failed: ${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      errMsg = j.message || j.title || errMsg;
    } catch (_) {}
    throw new Error(errMsg);
  }

  if (res.status === 204) return null;
  return res.json();
}

// ── Normalise API responses to the shape the UI expects ──────────────────────
// .NET returns PascalCase; we map everything to our camelCase shape.
function toProduct(p) {
  if (!p) return null;
  // Primary image: first image in Images array OR direct imageUrl field
  const primaryImage =
    p.images?.[0]?.imageUrl ??
    p.Images?.[0]?.ImageUrl ??
    p.imageUrl ??
    p.ImageUrl ??
    null;

  return {
    id: p.id ?? p.Id ?? 0,
    slug: p.slug ?? p.Slug ?? String(p.id ?? p.Id ?? ""),
    cat: p.categorySlug ?? p.CategorySlug ?? p.category ?? p.Category ?? "",
    price: Number(p.price ?? p.Price ?? 0),
    stock: p.stockStatus ?? p.StockStatus ?? p.stock ?? "In Stock",
    featured: Boolean(p.isFeatured ?? p.IsFeatured ?? false),
    emoji: p.emoji ?? p.Emoji ?? "🌱",
    bg: p.bg ?? p.Bg ?? "#e8f5e9",
    imageUrl: primaryImage, // ← uploaded image path e.g. "/uploads/product_abc.jpg"
    name: {
      en:
        p.name?.en ??
        p.Name?.En ??
        p.nameEn ??
        p.NameEn ??
        p.name ??
        p.Name ??
        "",
      hi: p.name?.hi ?? p.Name?.Hi ?? p.nameHi ?? p.NameHi ?? "",
      gu: p.name?.gu ?? p.Name?.Gu ?? p.nameGu ?? p.NameGu ?? "",
    },
    desc: {
      en:
        p.shortDesc?.en ??
        p.ShortDesc?.En ??
        p.desc?.en ??
        p.descEn ??
        p.DescEn ??
        p.description ??
        p.Description ??
        "",
      hi:
        p.shortDesc?.hi ??
        p.ShortDesc?.Hi ??
        p.desc?.hi ??
        p.descHi ??
        p.DescHi ??
        "",
      gu:
        p.shortDesc?.gu ??
        p.ShortDesc?.Gu ??
        p.desc?.gu ??
        p.descGu ??
        p.DescGu ??
        "",
    },
    type: {
      en:
        p.seedType?.en ??
        p.SeedType?.En ??
        p.type?.en ??
        p.typeEn ??
        p.TypeEn ??
        "",
      hi:
        p.seedType?.hi ??
        p.SeedType?.Hi ??
        p.type?.hi ??
        p.typeHi ??
        p.TypeHi ??
        "",
      gu:
        p.seedType?.gu ??
        p.SeedType?.Gu ??
        p.type?.gu ??
        p.typeGu ??
        p.TypeGu ??
        "",
    },
  };
}

function toCategory(c) {
  if (!c) return null;
  return {
    id: c.id ?? c.Id ?? 0,
    slug: c.slug ?? c.Slug ?? String(c.id ?? c.Id ?? ""),
    icon: c.icon ?? c.Icon ?? "🌱",
    count: Number(c.productCount ?? c.ProductCount ?? c.count ?? 0),
    from: c.colorFrom ?? c.ColorFrom ?? c.from ?? "#1a5c2e",
    to: c.colorTo ?? c.ColorTo ?? c.to ?? "#2d8a45",
    imageUrl: c.imageUrl ?? c.ImageUrl ?? null, // ← category image
    name: {
      en:
        c.name?.en ??
        c.Name?.En ??
        c.nameEn ??
        c.NameEn ??
        c.name ??
        c.Name ??
        "",
      hi: c.name?.hi ?? c.Name?.Hi ?? c.nameHi ?? c.NameHi ?? "",
      gu: c.name?.gu ?? c.Name?.Gu ?? c.nameGu ?? c.NameGu ?? "",
    },
  };
}

function toInquiry(i) {
  if (!i) return null;
  return {
    id: i.id ?? i.Id ?? 0,
    name: i.name ?? i.Name ?? i.customerName ?? i.CustomerName ?? "",
    phone: i.phone ?? i.Phone ?? i.phoneNumber ?? i.PhoneNumber ?? "",
    msg: i.message ?? i.Message ?? i.msg ?? "",
    status: i.status ?? i.Status ?? "New",
    date: i.date ?? i.Date ?? i.createdAt ?? i.CreatedAt ?? "",
  };
}

function toList(data, normFn) {
  if (!data) return [];
  const arr = Array.isArray(data)
    ? data
    : (data.items ?? data.data ?? data.results ?? []);
  return arr.map(normFn);
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH  —  POST /api/Auth/login
// ═══════════════════════════════════════════════════════════════════════════════
export const authService = {
  async login({ username, password }) {
    const data = await api("/Auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    const token =
      data.token ?? data.Token ?? data.accessToken ?? data.AccessToken ?? "";
    if (token) localStorage.setItem(getTokenKey(), token);
    return { token, username: data.username ?? data.Username ?? username };
  },

  logout() {
    localStorage.removeItem(getTokenKey());
  },

  isAuth() {
    return Boolean(getToken());
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCTS
// ═══════════════════════════════════════════════════════════════════════════════
export const productService = {
  // GET /api/Products
  async getAll(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const data = await api(`/Products${qs ? "?" + qs : ""}`);
    return toList(data, toProduct);
  },

  // GET /api/Products/featured
  async getFeatured() {
    const data = await api("/Products/featured");
    return toList(data, toProduct);
  },

  // GET /api/Products/{slug}
  async getBySlug(slug) {
    const data = await api(`/Products/${slug}`);
    return toProduct(data);
  },

  // POST /api/Products  [JWT]
  async create(payload) {
    const data = await api("/Products", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return toProduct(data);
  },

  // PUT /api/Products/{id}  [JWT]
  async update(id, payload) {
    const data = await api(`/Products/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return toProduct(data);
  },

  // DELETE /api/Products/{id}  [JWT]
  async remove(id) {
    return api(`/Products/${id}`, { method: "DELETE" });
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════════
export const categoryService = {
  // GET /api/Categories
  async getAll() {
    const data = await api("/Categories");
    return toList(data, toCategory);
  },

  // POST /api/Categories  [JWT]
  async create(payload) {
    const data = await api("/Categories", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return toCategory(data);
  },

  // PUT /api/Categories/{id}  [JWT]
  async update(id, payload) {
    const data = await api(`/Categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return toCategory(data);
  },

  // DELETE /api/Categories/{id}  [JWT]
  async remove(id) {
    return api(`/Categories/${id}`, { method: "DELETE" });
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// INQUIRIES
// ═══════════════════════════════════════════════════════════════════════════════
export const inquiryService = {
  // POST /api/Inquiries  (public — no JWT)
  async submit(payload) {
    return api("/Inquiries", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // GET /api/Inquiries  [JWT]
  async getAll() {
    const data = await api("/Inquiries");
    return toList(data, toInquiry);
  },

  // PUT /api/Inquiries/{id}/status  [JWT]
  async markReplied(id) {
    return api(`/Inquiries/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status: "Replied" }),
    });
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// UPLOAD SERVICE
//   POST   /api/Upload/image   → upload(file, prefix)  [JWT]
//   DELETE /api/Upload/image   → remove(path)          [JWT]
//
// Returns: { url: "/uploads/filename.jpg" }
// Full image URL = API_BASE_WITHOUT_API + url
//   e.g.  https://localhost:7001/uploads/product_abc.jpg
// ═══════════════════════════════════════════════════════════════════════════════
export const uploadService = {
  /**
   * Upload an image file. Returns the public URL path.
   * @param {File} file       — the File object from <input type="file">
   * @param {string} prefix   — "product" | "category" | "img"
   * @returns {Promise<string>} — e.g. "/uploads/product_20240315_abc.jpg"
   */
  async upload(file, prefix = "img") {
    const token = getToken();
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${BASE}/Upload/image?prefix=${prefix}`, {
      method: "POST",
      headers: {
        // Do NOT set Content-Type here — browser sets it with boundary automatically
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!res.ok) {
      let errMsg = `Upload failed: ${res.status}`;
      try {
        errMsg = (await res.json()).message || errMsg;
      } catch (_) {}
      throw new Error(errMsg);
    }

    const data = await res.json();
    // Return just the path: /uploads/filename.jpg
    return data.url ?? data.Url ?? "";
  },

  /**
   * Get the full displayable URL for an image path.
   * Pass the stored path from the DB, get back a full URL for <img src>.
   * @param {string} path — e.g. "/uploads/product_abc.jpg"
   * @returns {string}    — e.g. "https://localhost:7001/uploads/product_abc.jpg"
   */
  getUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path; // already a full URL
    // Strip /api from BASE to get the server root
    const serverRoot = BASE.replace(/\/api$/, "");
    return `${serverRoot}${path}`;
  },

  /**
   * Delete an uploaded image by its path. [JWT]
   * @param {string} path — e.g. "/uploads/product_abc.jpg"
   */
  async remove(path) {
    if (!path) return;
    return api(`/Upload/image?path=${encodeURIComponent(path)}`, {
      method: "DELETE",
    });
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// STATS SERVICE
//   GET /api/Stats        → getPublic()   — homepage hero + about counters
//   GET /api/Stats/admin  → getAdmin()    — admin dashboard cards [JWT]
// ═══════════════════════════════════════════════════════════════════════════════
export const statsService = {
  // Public stats — product count, years in business, etc.
  async getPublic() {
    const data = await api("/Stats");
    return {
      productCount: Number(data.productCount ?? data.ProductCount ?? 0),
      categoryCount: Number(data.categoryCount ?? data.CategoryCount ?? 0),
      inquiryCount: Number(data.inquiryCount ?? data.InquiryCount ?? 0),
      yearsInBusiness: Number(
        data.yearsInBusiness ?? data.YearsInBusiness ?? 30,
      ),
      citiesServed: Number(data.citiesServed ?? data.CitiesServed ?? 50),
      happyFarmers: Number(data.happyFarmers ?? data.HappyFarmers ?? 5000),
    };
  },

  // Admin dashboard stats [JWT]
  async getAdmin() {
    const data = await api("/Stats/admin");
    return {
      products: {
        total: Number(data.products?.total ?? 0),
        featured: Number(data.products?.featured ?? 0),
        inStock: Number(data.products?.inStock ?? 0),
        outOfStock: Number(data.products?.outOfStock ?? 0),
        addedThisWeek: Number(data.products?.addedThisWeek ?? 0),
      },
      categories: {
        total: Number(data.categories?.total ?? 0),
      },
      inquiries: {
        total: Number(data.inquiries?.total ?? 0),
        newCount: Number(data.inquiries?.newCount ?? 0),
        replied: Number(data.inquiries?.replied ?? 0),
        thisWeek: Number(data.inquiries?.thisWeek ?? 0),
      },
      company: {
        yearsInBusiness: Number(data.company?.yearsInBusiness ?? 30),
        happyFarmers: Number(data.company?.happyFarmers ?? 5000),
        citiesServed: Number(data.company?.citiesServed ?? 50),
      },
    };
  },
};
